import Drawer, { DrawerProps } from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import DeviceHub from '@material-ui/icons/DeviceHub';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { Theme, useTheme } from '@material-ui/core/styles';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import PieChartIcon from '@material-ui/icons/PieChart';
import { makeStyles } from '@material-ui/styles';
import React, { FunctionComponent, memo, useRef, useState } from 'react';
import {
  NavLink,
  NavLinkProps,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { useConfig } from '../../../eliot-components/src/ConfigProvider/hooks';
import {
  hasOneOfOrgRoles,
  hasRoleInAnyUsersOrg,
  hasUserOrgAnyRole,
  Role,
} from '../../../contexts/AuthProvider/types';
import {
  Chip,
  Dialog,
  Divider,
  FormControlLabel,
  IconButton,
  Popover,
  Switch,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { useManageContext } from '../../../contexts/ManageContext/ManageContext';
import { MediaDown } from '../../../components/MediaQueries/MediaQueries';
import CloseIcon from '@material-ui/icons/Close';
import SelectedOrg from './components/SelectedOrg/SelectedOrg';
import Auth from '@aws-amplify/auth';
import ChangePasswordDialog from '../../../eliot-components/src/ChangePasswordDialog/ChangePasswordDialog';
import Alerts from '../../../pages/Alerts/Alerts';
import {
  useCachedKnownIssues,
  useConfigurationWarning,
} from '../../../pages/Alerts/hooks';
import { useAuthUser } from '../../../contexts/AuthProvider/hooks';
import {
  useDeleteUserMutation,
  useGetTargetSystemsQuery,
  useUpdateUserMutation,
} from '../../../store/api-slice';
import { useSnackbar, VariantType } from 'notistack';
import Tooltip from '@material-ui/core/Tooltip';
import {
  calculateIsMaximumDevicesReached,
  isSelectedOrgDeactivated,
} from '../../../utils/helpers';
import { useSharedReports } from '../../../hooks/useSharedReports';
import { fetchWithAuth } from '../../../fetch';
import { parseErrorMessage } from '../../../eliot-components/src/utils/helpers';
import ConfirmationDialog from '../../../eliot-components/src/ConfirmationDialog/ConfirmationDialog';
const drawerWidth = 230;

// required for react-router-dom < 6.0.0
// see https://github.com/ReactTraining/react-router/issues/6056#issuecomment-435524678
const AdapterNavLink = React.forwardRef<HTMLAnchorElement, NavLinkProps>(
  (props, ref) => <NavLink innerRef={ref as any} {...props} />
);

const useStyles = makeStyles((theme: Theme) => ({
  username: {
    wordBreak: 'break-all',
  },
  drawer: {
    flexShrink: 0,
    transition: 'width 225ms',
    [theme.breakpoints.down('md')]: {
      width: 300,
    },
  },
  drawerPaper: {
    minWidth: drawerWidth,
    transition: 'none !important',
    [theme.breakpoints.down('md')]: {
      width: 300,
    },

    '& .active': {
      background: 'rgba(0, 0, 0, 0.2)',
    },
  },
  collapseHidden: {
    visibility: 'hidden',
    width: '0px',
    minWidth: '0px',
    height: '0px',
    minHeight: '0px',
  },
  userSidebar: {
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'block',
    },
  },
  toolbar: theme.mixins.toolbar,
  close: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    height: '100%',
    paddingRight: '2px',
    backgroundColor: theme.palette.primary.main,
    '& button': {
      color: theme.palette.primary.contrastText,
    },
  },
  orgs: {
    padding: theme.spacing(1, 2),
  },
  alertsCount: {
    textAlign: 'right',
  },
  darkModeLabel: {
    '&.MuiFormControlLabel-labelPlacementStart': {
      marginLeft: 0,
    },
  },
}));

const MainDrawer: FunctionComponent = memo(() => {
  const classes = useStyles();
  const config = useConfig();
  const theme = useTheme();
  const { authUser } = useAuthUser();
  const location = useLocation();
  const history = useHistory();
  const { state, dispatch, toggleDarkMode, isDarkModeActive } =
    useManageContext();
  const {
    state: { numberOfIssues },
    isLoading: isLoadingKI,
  } = useCachedKnownIssues();

  const { isLoading: isLoadingTS, data: targetSystems = [] } =
    useGetTargetSystemsQuery({
      apiBaseURL: config.apiBaseURL,
      orgs: authUser.selectedOrgs,
      filters: [],
    });

  const reports = useSharedReports();
  const isMaximumDevicesReached = calculateIsMaximumDevicesReached(
    authUser,
    reports
  );

  const maximumDevicesReachedMessage =
    'Maximum selected devices or organizations is reached.You need deselect some of the orgs to be able to view this section';

  const isOrgDisabled = isSelectedOrgDeactivated(authUser);

  if (
    (isMaximumDevicesReached || isOrgDisabled) &&
    location.pathname.indexOf('/dashboard') === -1
  ) {
    history.push('/dashboard');
  }
  const incompatibleTs = useConfigurationWarning(reports, targetSystems);
  const { enqueueSnackbar } = useSnackbar();
  const totalNumberOfIssues =
    isLoadingKI || isLoadingTS
      ? 0
      : numberOfIssues +
        targetSystems.filter(
          (ts) => ts.lastStatusCode && ts.lastStatusCode >= 400
        ).length +
        incompatibleTs.length;
  const isBigScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const alertsRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  let variant: DrawerProps['variant'] = 'temporary';
  const { user } = state;
  const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] =
    useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  let username = '';
  let showTargetSystems = false;
  let showDevices = false;

  if (user) {
    username = user.givenName || user.nickname || user.email;

    showTargetSystems = hasOneOfOrgRoles(user, user.selectedOrgs, [
      Role.DeviceManager,
      Role.DeviceViewer,
    ]);
    showDevices = hasOneOfOrgRoles(user, user.selectedOrgs, [
      Role.DeviceManager,
      Role.DeviceViewer,
    ]);
  }

  let visible = state.isSidebarVisible;
  let visibleBigScreen = true;

  if (isBigScreen) {
    variant = 'persistent';
    visibleBigScreen = !(visible ? visible : false);
  }

  const handleLogout = async () => {
    window.location.replace('/logout');
  };

  const handleChangePassword = (isSuccess: boolean, hasError: boolean) => {
    if (isSuccess) {
      enqueueSnackbar('Password successfully changed', {
        variant: 'success',
      });
      setIsChangePasswordDialogOpen(false);
      if (authUser.needResetPassword) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    }
  };

  const handleAPIKeyClipboard = async () => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    navigator.clipboard.writeText('Bearer ' + token);
    enqueueSnackbar('API Key copied to clipboard', { variant: 'info' });
  };
  const apiKey = hasUserOrgAnyRole(authUser) ? (
    <ListItem button onClick={handleAPIKeyClipboard}>
      <ListItemText>API Key</ListItemText>
    </ListItem>
  ) : null;

  const [updateUser] = useUpdateUserMutation();
  const { apiBaseURL } = useConfig();
  const [isLoading, setIsLoading] = useState(false);
  const totalMeasurementMode =
    localStorage.getItem('totalMeasurementMode') === 'true';

  const [deleteUser, { isLoading: deleteLoading }] = useDeleteUserMutation();
  return (
    <Drawer
      variant={variant}
      open={isBigScreen ? visibleBigScreen : visible}
      className={classes.drawer}
      classes={{
        paper: classes.drawerPaper,
      }}
      onClose={() => dispatch({ type: 'sidebar-changed' })}
      style={{ width: visibleBigScreen ? drawerWidth : 0 }}
    >
      <div className={classes.toolbar}>
        <MediaDown query="md">
          <div className={classes.close}>
            <IconButton
              onClick={() => {
                dispatch({ type: 'sidebar-changed' });
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </MediaDown>
      </div>
      <List className={classes.userSidebar}>
        <ListItem className={classes.username}>
          <Typography variant="h5">{username}</Typography>
        </ListItem>

        <SelectedOrg />

        <ListItem button onClick={handleLogout}>
          <ListItemText>Logout</ListItemText>
        </ListItem>

        <ListItem button onClick={toggleDarkMode}>
          <FormControlLabel
            labelPlacement="start"
            className={classes.darkModeLabel}
            control={
              <Switch
                color="primary"
                checked={isDarkModeActive}
                onClick={toggleDarkMode}
              />
            }
            label="Dark mode"
          />
        </ListItem>

        <ListItem
          button
          disabled={isLoading}
          onClick={() => {
            if (totalMeasurementMode) {
              localStorage.removeItem('totalMeasurementMode');
            } else {
              localStorage.setItem('totalMeasurementMode', 'true');
            }
            window.location.reload();
          }}
        >
          <FormControlLabel
            labelPlacement="start"
            className={classes.darkModeLabel}
            control={<Switch color="primary" checked={totalMeasurementMode} />}
            label="Measurement Total"
          />
        </ListItem>

        {((authUser.globalRoles && authUser.globalRoles.length > 0) ||
          authUser?.globalRolesModeEnabled) && (
          <ListItem
            button
            disabled={
              isLoading ||
              !hasRoleInAnyUsersOrg(
                authUser,
                [Role.DeviceManager, Role.DeviceViewer],
                authUser.orgs
              )
            }
            onClick={() => {
              setIsLoading(true);
              const enabled =
                authUser.globalRoles && authUser.globalRoles.length > 0;
              updateUser({
                data: { globalRolesModeEnabled: !enabled },
                apiBaseURL,
                id: authUser.id,
              }).then(() => {
                // if enabled, don't load all orgs if we have a selected org
                if (enabled) {
                  window.localStorage.removeItem('user.selected_org');
                }
                window.location.reload();
              });
            }}
          >
            <FormControlLabel
              labelPlacement="start"
              className={classes.darkModeLabel}
              control={
                <Switch
                  color="primary"
                  checked={
                    authUser.globalRoles && authUser.globalRoles.length > 0
                  }
                />
              }
              label="Global mode"
            />
          </ListItem>
        )}

        <ListItem
          button
          onClick={() => {
            setIsChangePasswordDialogOpen(true);
          }}
        >
          <ListItemText>Change Password</ListItemText>
        </ListItem>

        {apiKey}

        <ListItem
          button
          onClick={() => {
            setIsDeleteDialogOpen(true);
          }}
        >
          <ListItemText>Delete user account</ListItemText>
        </ListItem>
      </List>
      <Divider />
      <List>
        <ListItem button component={AdapterNavLink} to="/dashboard">
          <ListItemIcon>
            <PieChartIcon />
          </ListItemIcon>
          <ListItemText primary="Dashboard" disableTypography />
        </ListItem>

        <Tooltip
          title={maximumDevicesReachedMessage}
          disableFocusListener={!isMaximumDevicesReached}
          disableHoverListener={!isMaximumDevicesReached}
          disableTouchListener={!isMaximumDevicesReached}
        >
          <div>
            <ListItem
              button
              component={
                isMaximumDevicesReached || isOrgDisabled || !showDevices
                  ? ListItem
                  : AdapterNavLink
              }
              to="/devices"
              disabled={
                isMaximumDevicesReached || isOrgDisabled || !showDevices
              }
            >
              <ListItemIcon>
                <PhoneAndroidIcon />
              </ListItemIcon>
              <ListItemText primary="Devices" disableTypography />
            </ListItem>
          </div>
        </Tooltip>

        <Tooltip
          title={maximumDevicesReachedMessage}
          disableFocusListener={!isMaximumDevicesReached}
          disableHoverListener={!isMaximumDevicesReached}
          disableTouchListener={!isMaximumDevicesReached}
        >
          <div>
            <ListItem
              button
              component={
                isMaximumDevicesReached || isOrgDisabled || !showTargetSystems
                  ? ListItem
                  : AdapterNavLink
              }
              to="/webhooks"
              disabled={
                isMaximumDevicesReached || isOrgDisabled || !showTargetSystems
              }
            >
              <ListItemIcon>
                <DeviceHub />
              </ListItemIcon>
              <ListItemText primary="Webhooks" disableTypography />
            </ListItem>
          </div>
        </Tooltip>

        {config.isTagsFeatureEnabled && (
          <ListItem button component={AdapterNavLink} to="/tags">
            <ListItemIcon>
              <LocalOfferIcon />
            </ListItemIcon>
            <ListItemText primary="Tags" disableTypography />
          </ListItem>
        )}
        <Tooltip
          title={maximumDevicesReachedMessage}
          disableFocusListener={!isMaximumDevicesReached}
          disableHoverListener={!isMaximumDevicesReached}
          disableTouchListener={!isMaximumDevicesReached}
        >
          <div>
            <ListItem
              button
              ref={alertsRef}
              onClick={() => setIsOpen(!isOpen)}
              disabled={
                isMaximumDevicesReached ||
                isOrgDisabled ||
                !showTargetSystems ||
                !showDevices
              }
            >
              <ListItemIcon>
                <ReportProblemIcon />
              </ListItemIcon>
              <ListItemText primary="Alerts" disableTypography />
              <ListItemText className={classes.alertsCount}>
                <Chip label={totalNumberOfIssues.toLocaleString()} />
              </ListItemText>
            </ListItem>
          </div>
        </Tooltip>
      </List>
      {isBigScreen ? (
        <Popover
          anchorEl={alertsRef.current}
          open={isOpen}
          onClose={() => setIsOpen(false)}
          anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        >
          <Alerts onItemClicked={() => setIsOpen(false)} />
        </Popover>
      ) : (
        <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
          <Alerts onItemClicked={() => setIsOpen(false)} />
        </Dialog>
      )}
      <ChangePasswordDialog
        onConfirm={handleChangePassword}
        onClose={() => {
          setIsChangePasswordDialogOpen(false);
        }}
        isOpen={isChangePasswordDialogOpen}
        needResetPassword={authUser.needResetPassword || false}
        onNotification={(msg, variant) => {
          enqueueSnackbar(msg, { variant: variant as VariantType });
        }}
        handleLogout={async () => {
          try {
            await Auth.signOut({ global: true });
          } catch {
            await Auth.signOut();
          } finally {
            window.location.replace('/login');
          }
        }}
        updateUserFlag={() =>
          fetchWithAuth(`${apiBaseURL}/v1/users/${authUser.id}`, {
            method: 'PATCH',
            headers: { Accept: 'application/json' },
            body: JSON.stringify({ needResetPassword: false }),
          })
        }
      ></ChangePasswordDialog>
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
        }}
        onConfirm={async () => {
          try {
            const result = await deleteUser({ apiBaseURL, id: authUser.id });
            if ('error' in result) {
              enqueueSnackbar(parseErrorMessage(result.error), {
                variant: 'error',
              });
              setIsDeleteDialogOpen(false);
            } else {
              handleLogout();
            }
          } catch (error) {
            enqueueSnackbar(error, {
              variant: 'error',
            });
            setIsDeleteDialogOpen(false);
          }
        }}
        isLoading={deleteLoading}
        questionText="Are you sure you want to delete this user account?"
        confirmationText="Delete"
        cancelText="Cancel"
        title="Delete user account"
        inputCheckName={authUser.email}
      />
    </Drawer>
  );
});

export default MainDrawer;
